import { useCallback } from 'react';
import { Navigate, Outlet, useMatch, useNavigate } from 'react-router-dom';

import { animationUtil } from '@pukka/common/utils';
import { Button, DatePicker, Loader, Menu, PageContainer, SelectV2, Tab } from '@pukka/ui-components';
import { DefaultActionBar, SearchBar } from '@pukka/ui-composite';
import { twMerge } from 'tailwind-merge';
import { useSnapshot } from 'valtio';

import {
  CancelledTransactionState,
  SuccessTransactionState,
  TAB,
  TransactionPersistedState,
  TransactionState,
  VoidTransactionState,
  onChangeSearch,
  onExportTransactions,
  onOutletChange,
  onSetDate,
  useTransactionData,
} from './Transaction.state';
import { getExportTitle, getLoadingValue, getSearchPlaceholder, getSearchValue } from './utils';

const TAB_DATA: { value: TAB; title: string }[] = [
  {
    value: 'success',
    title: 'Success Orders',
  },
  {
    value: 'cancelled',
    title: 'Cancelled Orders',
  },
  {
    value: 'void',
    title: 'Void Items',
  },
];

const DEFAULT_TAB = TAB_DATA[0].value;
const DATES = [null, new Date()] as [null, Date];

function TransactionPage() {
  const match = useMatch('v2/reports/transactions/:tabId/:id?/:token?');
  const navigate = useNavigate();
  const isDetailOpen = !!match?.params.id;
  const currentTabId = match?.params.tabId as TAB | undefined;
  const { outlets } = useSnapshot(TransactionState);
  const { selectedOutletId, startDate, endDate, receipt, itemName, orderId, allDay } = useSnapshot(TransactionPersistedState);
  const { exports } = useSnapshot(SuccessTransactionState);
  const { isExporting: isCancelledExporting } = useSnapshot(CancelledTransactionState);
  const { isExporting: isVoidExporting } = useSnapshot(VoidTransactionState);

  useTransactionData();

  const onChangeTab = useCallback(
    (tab: string) => {
      animationUtil.doTransition(() => {
        navigate(`./${tab}`, {
          relative: 'path',
        });
      });
    },
    [navigate],
  );

  const onCloseDetail = useCallback(() => {
    if (isDetailOpen) {
      navigate(`/v2/reports/transactions/${currentTabId}`, {
        unstable_viewTransition: true,
        preventScrollReset: true,
      });
    }
  }, [navigate, currentTabId, isDetailOpen]);

  if (!currentTabId) {
    return <Navigate to={DEFAULT_TAB} />;
  }

  return (
    // The overflow-visible is crucial to make the sticky transaction detail works
    <PageContainer className="w-full overflow-visible">
      <DefaultActionBar pageTitle="Transactions" />
      <Tab data={TAB_DATA} value={currentTabId} onChange={onChangeTab} />
      <div className="mb-4 flex flex-row gap-x-3">
        <SelectV2
          options={outlets}
          value={selectedOutletId}
          // eslint-disable-next-line react/jsx-no-bind
          onChange={onOutletChange.bind({}, currentTabId, onCloseDetail)}
          className="w-60"
        />
        <DatePicker
          allDay={allDay}
          timePicker
          rangePicker
          startDate={startDate}
          endDate={endDate}
          dates={DATES}
          // eslint-disable-next-line react/jsx-no-bind
          onChangeDate={onSetDate.bind({}, currentTabId, onCloseDetail)}
        />
        {currentTabId === 'success' ? (
          <SearchBar
            key={currentTabId}
            defaultValue={getSearchValue(currentTabId, receipt, itemName, orderId)}
            placeholder={getSearchPlaceholder(currentTabId)}
            // eslint-disable-next-line react/jsx-no-bind
            onKeywordChange={onChangeSearch.bind({}, currentTabId, onCloseDetail)}
            className="view-transition-[tx-search]"
          />
        ) : null}
        {currentTabId === 'success' ? (
          <Menu
            align="end"
            trigger={
              <Button icon="chevron-down" reverse className="ml-auto">
                Export
              </Button>
            }
          >
            {(() => {
              const disabled = exports.has('order-export-transaction');

              return (
                <Menu.Option
                  disabled={disabled}
                  // eslint-disable-next-line react/jsx-no-bind
                  onClick={onExportTransactions.bind({}, currentTabId, 'order-export-transaction')}
                  className={twMerge('justify-end', disabled ? 'text-neutral-500' : '')}
                >
                  {getExportTitle(currentTabId)}
                  {disabled ? <Loader size={16} className="ml-2" /> : null}
                </Menu.Option>
              );
            })()}
            {(() => {
              const disabled = exports.has('order-export-item-detail');

              return (
                <Menu.Option
                  disabled={disabled}
                  // eslint-disable-next-line react/jsx-no-bind
                  onClick={onExportTransactions.bind({}, currentTabId, 'order-export-item-detail')}
                  className={twMerge('justify-end', disabled ? 'text-neutral-500' : '')}
                >
                  Item Details
                  {disabled ? <Loader size={16} className="ml-2" /> : null}
                </Menu.Option>
              );
            })()}
          </Menu>
        ) : (
          <Button
            loading={getLoadingValue(currentTabId, isCancelledExporting, isVoidExporting)}
            disabled={selectedOutletId === -1}
            // eslint-disable-next-line react/jsx-no-bind
            onClick={onExportTransactions.bind({}, currentTabId, undefined)}
            className="ml-auto"
          >
            Export
          </Button>
        )}
      </div>
      <Outlet />
    </PageContainer>
  );
}

export { TransactionPage };
